import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, CheckCircle, Loader2 } from 'lucide-react';
import { supabase } from '../lib/supabase';

const StripeWebhookHandler: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    const verifyWebhookAccess = async () => {
      try {
        // Check if user is admin
        const { data: { user }, error: authError } = await supabase.auth.getUser();
        
        if (authError) {
          throw new Error('Authentication error: ' + authError.message);
        }

        if (!user) {
          navigate('/login');
          return;
        }

        // Check if user is admin
        const { data: profile, error: profileError } = await supabase
          .from('profiles')
          .select('admin_user')
          .eq('id', user.id)
          .single();

        if (profileError) {
          throw new Error('Error fetching profile: ' + profileError.message);
        }

        if (!profile?.admin_user) {
          navigate('/');
          return;
        }

        // Verify Stripe webhook configuration
        const { data: webhookConfig, error: webhookError } = await supabase
          .from('app_config')
          .select('value')
          .eq('key', 'STRIPE_WEBHOOK_SECRET')
          .single();

        if (webhookError) {
          throw new Error('Error fetching webhook configuration: ' + webhookError.message);
        }

        if (!webhookConfig?.value) {
          throw new Error('Stripe webhook secret not configured');
        }

        setSuccess('Stripe webhook is properly configured');
        setLoading(false);
      } catch (err) {
        console.error('Error verifying webhook access:', err);
        setError(err instanceof Error ? err.message : 'Unknown error occurred');
        setLoading(false);
      }
    };

    verifyWebhookAccess();
  }, [navigate]);

  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full">
        <h1 className="text-2xl font-bold mb-6 text-center">Stripe Webhook Handler</h1>
        
        {loading ? (
          <div className="flex flex-col items-center justify-center py-8">
            <Loader2 className="w-12 h-12 text-purple-600 animate-spin mb-4" />
            <p className="text-gray-600">Verifying webhook configuration...</p>
          </div>
        ) : error ? (
          <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6">
            <div className="flex items-center">
              <AlertCircle className="w-6 h-6 text-red-500 mr-3" />
              <p className="text-red-700">{error}</p>
            </div>
          </div>
        ) : (
          <div className="bg-green-50 border-l-4 border-green-500 p-4 mb-6">
            <div className="flex items-center">
              <CheckCircle className="w-6 h-6 text-green-500 mr-3" />
              <p className="text-green-700">{success}</p>
            </div>
          </div>
        )}

        <div className="mt-6">
          <h2 className="text-lg font-semibold mb-3">Webhook Information</h2>
          <div className="bg-gray-50 p-4 rounded-lg">
            <p className="text-sm text-gray-700 mb-2">
              <span className="font-medium">Webhook URL:</span> {window.location.origin}/stripe-webhook
            </p>
            <p className="text-sm text-gray-700 mb-2">
              <span className="font-medium">Events to listen for:</span>
            </p>
            <ul className="list-disc list-inside text-sm text-gray-700 ml-2">
              <li>checkout.session.completed</li>
              <li>payment_intent.succeeded</li>
              <li>payment_intent.payment_failed</li>
              <li>customer.subscription.created</li>
              <li>customer.subscription.updated</li>
              <li>customer.subscription.deleted</li>
            </ul>
          </div>
        </div>

        <div className="mt-6 text-center">
          <button
            onClick={() => navigate('/admin/dashboard')}
            className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    </div>
  );
};

export default StripeWebhookHandler;